import { useState } from 'react';
import { Plus, Pencil, CheckCircle } from 'lucide-react';
import { Button, Badge, Modal, Input, EmptyState } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useCompanies,
  useTaxes,
  useCreateTax,
  useUpdateTax,
  useMarkTaxPaid,
} from '../../../hooks/useFinancialOps';

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'warning' | 'danger' | 'info'> = {
  PENDING: 'warning',
  PAID: 'accent',
  OVERDUE: 'danger',
};
const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PAID: 'Pago',
  OVERDUE: 'Vencido',
};

const TAX_REGIME_LABELS: Record<string, string> = {
  SIMPLES_NACIONAL: 'Simples Nacional',
  LUCRO_PRESUMIDO: 'Lucro Presumido',
  LUCRO_REAL: 'Lucro Real',
  MEI: 'MEI',
};

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
}

const MONTH_NAMES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

interface TaxFormData {
  companyId: string;
  name: string;
  taxRegime: string;
  rate: string;
  amount: string;
  month: string;
  year: string;
  dueDate: string;
  notes: string;
}

const emptyForm = (companyId: string): TaxFormData => {
  const now = new Date();
  return {
    companyId,
    name: '',
    taxRegime: 'SIMPLES_NACIONAL',
    rate: '',
    amount: '',
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
    dueDate: '',
    notes: '',
  };
};

export function ImpostosPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const params: Record<string, any> = { companyId: selectedCompanyId };
  if (filterYear) {
    params.year = Number(filterYear);
  }
  if (filterMonth) {
    params.month = Number(filterMonth);
  }
  if (filterStatus) {
    params.status = filterStatus;
  }

  const { data, isLoading } = useTaxes(params);
  const createTax = useCreateTax();
  const updateTax = useUpdateTax();
  const markPaid = useMarkTaxPaid();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TaxFormData>(emptyForm(''));

  const taxes = data?.data ?? [];

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm(selectedCompanyId));
    setShowModal(true);
  }

  function openEdit(tax: any) {
    setEditingId(tax.id);
    setForm({
      companyId: tax.companyId,
      name: tax.name ?? '',
      taxRegime: tax.taxRegime ?? 'SIMPLES_NACIONAL',
      rate: String(tax.rate * 100),
      amount: String(tax.amount),
      month: String(tax.month),
      year: String(tax.year),
      dueDate: tax.dueDate?.slice(0, 10) ?? '',
      notes: tax.notes ?? '',
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, any> = {
      ...form,
      rate: parseFloat(form.rate) / 100,
      amount: parseFloat(form.amount),
      month: Number(form.month),
      year: Number(form.year),
    };
    if (!payload.dueDate) {
      delete payload.dueDate;
    }
    if (!payload.notes) {
      delete payload.notes;
    }

    if (editingId) {
      await updateTax.mutateAsync({ id: editingId, data: payload });
    } else {
      await createTax.mutateAsync(payload);
    }
    setShowModal(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Empresa
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink min-w-[200px]"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Ano
          </label>
          <input
            type="number"
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink w-24"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            min="2000"
            max="2100"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Mês
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
          >
            <option value="">Todos</option>
            {MONTH_NAMES.map((m, i) => (
              <option key={i + 1} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Status
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="PENDING">Pendente</option>
            <option value="PAID">Pago</option>
            <option value="OVERDUE">Vencido</option>
          </select>
        </div>
        {selectedCompanyId && (
          <Button onClick={openCreate} size="sm">
            <Plus size={16} className="mr-1" /> Novo Imposto
          </Button>
        )}
      </div>

      {!selectedCompanyId ? (
        <EmptyState
          title="Selecione uma empresa"
          body="Escolha uma empresa para ver os impostos."
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : taxes.length === 0 ? (
        <EmptyState title="Nenhum imposto cadastrado" body="Clique em Novo Imposto para começar." />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Nome</th>
                <th className="px-4 py-2 text-left font-medium">Regime</th>
                <th className="px-4 py-2 text-right font-medium">Alíquota</th>
                <th className="px-4 py-2 text-right font-medium">Valor</th>
                <th className="px-4 py-2 text-left font-medium">Período</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {taxes.map((tax: any) => (
                <tr key={tax.id} className="border-t border-border hover:bg-subtle/50">
                  <td className="px-4 py-3 text-ink font-medium">{tax.name}</td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {TAX_REGIME_LABELS[tax.taxRegime] ?? tax.taxRegime}
                  </td>
                  <td className="px-4 py-3 text-right text-ink-secondary">
                    {(tax.rate * 100).toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-ink">
                    {formatCurrency(tax.amount)}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {MONTH_NAMES[tax.month - 1]}/{tax.year}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[tax.status] ?? 'neutral'}>
                      {STATUS_LABELS[tax.status] ?? tax.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {tax.status !== 'PAID' && (
                        <button
                          onClick={() => markPaid.mutate({ id: tax.id })}
                          className="text-ink-secondary hover:text-accent"
                          title="Marcar como pago"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(tax)}
                        className="text-ink-secondary hover:text-ink-primary"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Imposto' : 'Novo Imposto'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="ex: DAS Simples Nacional"
            required
          />
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Regime Tributário *
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.taxRegime}
              onChange={(e) => setForm((f) => ({ ...f, taxRegime: e.target.value }))}
            >
              {Object.entries(TAX_REGIME_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Alíquota (%) *"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.rate}
              onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
              required
            />
            <Input
              label="Valor (R$) *"
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
                Mês *
              </label>
              <select
                className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
                value={form.month}
                onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={i + 1} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Ano *"
              type="number"
              min="2000"
              max="2100"
              value={form.year}
              onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
              required
            />
          </div>
          <Input
            label="Vencimento"
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
          />
          <Input
            label="Observações"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={createTax.isPending || updateTax.isPending}>
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
